/**
 * parseInlineCitations
 * ────────────────────
 * Scans assistant message text and converts well-formed Italian/EU legal
 * citations into structured tokens the UI can render as clickable badges.
 *
 * Supported patterns (case-insensitive, conservative — we never want to
 * false-positive on regular text):
 *   - Cass. [SS.UU. | Sez. <I-X>] n. <NUM>/<YEAR>
 *   - C. cost. n. <NUM>/<YEAR>
 *   - CGUE C-<NUM>/<YY>
 *   - art. <NUM>(<bis|ter|quater>)? c.c. | c.p. | c.p.c. | c.p.p.
 *   - L. <NUM>/<YEAR>  · D.lgs. <NUM>/<YEAR>  · D.l. <NUM>/<YEAR>
 *   - GDPR art. <NUM>
 *
 * Returns: an Array of { type: "text" | "cite", text: string, kind, query }
 *  - `text`  → render as raw text
 *  - `cite`  → render as clickable badge; `query` is what to send to
 *              /api/legal/jurisprudence/search (or generic legal lookup)
 *  - `kind`  → "ruling" | "law" | "code" → used to pick badge color/icon
 */

// Build the master regex from named sub-patterns. We use a single regex with
// alternation so the matches arrive in document order (essential for splicing).
// NOTE: patterns intentionally tolerate commas/spaces inside the section
// indicator because Italian legal writing varies ("Cass. SS.UU.", "Cass. civ.,
// Sez. III", "Cass. pen. Sez. lav."). The trailing `n. <NUM>/<YEAR>` anchors
// the match.
const RX = new RegExp(
  [
    // Italian Supreme Court (Cassazione) — civil & criminal sections.
    // Allows things like: "Cass. civ., Sez. III, n. 8268/2008", "Cass. SS.UU. n. 8268/2008".
    "(?<ruling>Cass(?:\\.|azione)?(?:[\\s,]+(?:civ\\.?|pen\\.?|SS\\.UU\\.?|Sez\\.?\\s*(?:Lav\\.?|U\\.?|[IVX]+)))*[\\s,]*n\\.?\\s*\\d{1,6}\\/\\d{2,4})",
    // Constitutional Court
    "(?<consult>C\\.?\\s*[Cc]ost\\.?\\s*n\\.?\\s*\\d{1,5}\\/\\d{2,4})",
    // Court of Justice of the EU
    "(?<cgue>CGUE\\s+C-\\d{1,4}\\/\\d{2,4})",
    // Italian articles (codes) — must match word-boundary at end so "c.c." is fine
    "(?<art>art\\.?\\s*\\d{1,4}(?:[\\-\\s]?(?:bis|ter|quater|quinquies|sexies))?\\s+c\\.?\\s?(?:c|p|p\\.c|p\\.p)\\.?(?![a-z]))",
    // National laws & decrees
    "(?<law>(?:L\\.|Legge|D\\.lgs\\.|D\\.l\\.|D\\.M\\.|D\\.P\\.R\\.)\\s+n?\\.?\\s*\\d{1,4}\\/\\d{2,4})",
    // GDPR articles — both orderings: "GDPR art. 17" and "art. 17 GDPR"
    "(?<gdpr1>GDPR\\s+art\\.?\\s*\\d{1,3}(?:\\.\\d+)?)",
    "(?<gdpr2>(?:[Aa]rt\\.?|articolo)\\s*\\d{1,3}(?:\\.\\d+)?\\s+GDPR\\b)",
  ].join("|"),
  "gi"
);

function _kindFor(matchGroups) {
  if (matchGroups.ruling || matchGroups.consult || matchGroups.cgue) return "ruling";
  if (matchGroups.art) return "code";
  if (matchGroups.law || matchGroups.gdpr1 || matchGroups.gdpr2) return "law";
  return "ruling";
}

export function parseInlineCitations(text) {
  if (!text || typeof text !== "string") return [{ type: "text", text: text || "" }];

  const out = [];
  let lastIdx = 0;
  for (const m of text.matchAll(RX)) {
    if (m.index > lastIdx) {
      out.push({ type: "text", text: text.slice(lastIdx, m.index) });
    }
    const matched = m[0];
    const kind = _kindFor(m.groups || {});
    out.push({
      type: "cite",
      text: matched,
      kind,
      // The search query strips noise (extra spaces, leading "Cass.").
      // We keep the original form for display but use a clean query string.
      query: matched.replace(/\s+/g, " ").trim(),
    });
    lastIdx = m.index + matched.length;
  }
  if (lastIdx < text.length) {
    out.push({ type: "text", text: text.slice(lastIdx) });
  }
  return out;
}

// Quick boolean check — used by the UI to decide whether to render the
// post-processed message vs the plain string (avoids re-parsing for free chats).
export function hasInlineCitations(text) {
  if (!text || typeof text !== "string") return false;
  RX.lastIndex = 0;
  const has = RX.test(text);
  RX.lastIndex = 0;
  return has;
}
